import { useState, useEffect } from 'react';
import { ProviderMetadata } from '../types';
import { fetchProviderMetadata } from '../services/api';

// Module-level cache so re-mounts don't re-fetch
let cachedProviders: ProviderMetadata[] | null = null;
let fetchPromise: Promise<ProviderMetadata[]> | null = null;

export interface UseProviderMetadataResult {
  providers: ProviderMetadata[];
  loading: boolean;
  error: string | null;
}

export function useProviderMetadata(): UseProviderMetadataResult {
  const [providers, setProviders] = useState<ProviderMetadata[]>(cachedProviders ?? []);
  const [loading, setLoading] = useState(!cachedProviders);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cachedProviders) {
      setProviders(cachedProviders);
      setLoading(false);
      return;
    }

    // Deduplicate concurrent fetches
    if (!fetchPromise) {
      fetchPromise = fetchProviderMetadata()
        .then((res) => {
          cachedProviders = res.providers;
          return res.providers;
        })
        .catch((err) => {
          fetchPromise = null; // Allow retry on next mount
          throw err;
        });
    }

    let cancelled = false;
    fetchPromise
      .then((data) => {
        if (!cancelled) {
          setProviders(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load provider metadata');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, []);

  return { providers, loading, error };
}
